import type { IndexDB } from "~~/types/index-db";
import constants from "~~/constants";
import { openDB, type IDBPDatabase } from "idb";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;
const OPEN_TIMEOUT_MS = 5000;

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("IndexedDB open timed out")),
      ms,
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function openIndexDb() {
  let db: IDBPDatabase<IndexDB> | undefined;
  return openDB<IndexDB>(constants.IDBNAME, constants.IDBVERSION, {
    upgrade(database) {
      if (!database.objectStoreNames.contains("submissions")) {
        database.createObjectStore("submissions", {
          keyPath: "id",
          autoIncrement: true,
        });
      }
      if (!database.objectStoreNames.contains("files")) {
        database.createObjectStore("files", {
          keyPath: "submissionId",
          autoIncrement: false,
        });
      }
      if (!database.objectStoreNames.contains("forms")) {
        database.createObjectStore("forms", {
          keyPath: "id",
          autoIncrement: true,
        });
      }
    },
    // another connection (e.g. a stale tab/service worker) is delaying this upgrade
    blocked() {},
    // this connection is delaying a newer one elsewhere; release it so that one can proceed
    blocking() {
      db?.close();
    },
  }).then((database) => {
    db = database;
    return database;
  });
}

export default async function getIndexDbPromise(
  retries = MAX_RETRIES,
): Promise<IDBPDatabase<IndexDB>> {
  try {
    return await withTimeout(openIndexDb(), OPEN_TIMEOUT_MS);
  } catch (error) {
    if (retries <= 0) throw error;
    await delay(RETRY_DELAY_MS * (MAX_RETRIES - retries + 1));
    return getIndexDbPromise(retries - 1);
  }
}
